import { Tooltip } from "@mui/material";
import {
  AddIcon,
  ImportIcon,
  DriveFileMoveIcon,
} from "../../assets/composer-editor-icons";
import {
  buildCourseSkillEditPath,
  buildCourseSkillGroupEditPath,
  buildCourseSkillGroupNewPath,
  buildCourseSkillNewPath,
  buildCourseSkillsPath,
} from "../../pages/skills/skillsPaths";
import {
  CONTENT_ITEM_ROW_PAD,
  PROGRAM_TREE_NODE_PAD,
  UNIT_TREE_NODE_PAD,
} from "./contentEditorSidebarConstants";
import TreeNode from "./ContentEditorTreeNode";
import NodeActivationBadge from "../shared/activation/NodeActivationBadge";

/**
 * One trailing action on a tree row. The click must not reach the row
 * underneath - without the `stopPropagation` the sidebar navigates away the
 * moment you start an import.
 *
 * `isImport` carries two things that only the import actions want: the
 * `--import` style modifier, and a `<span>` host for the tooltip. The host is
 * required because MUI cannot attach a listener to a disabled button, so an
 * import tooltip would vanish exactly while the control is disabled mid-import -
 * the one moment the user needs it to explain itself. The Add actions are never
 * disabled and deliberately render bare.
 */
const TreeAction = ({ title, label, icon, isImport = false, disabled = false, onClick }) => {
  const button = (
    <button
      type="button"
      className={`pd-tree-node-action${isImport ? " pd-tree-node-action--import" : ""}`}
      aria-label={label}
      disabled={isImport ? disabled : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {icon}
    </button>
  );

  return (
    <Tooltip title={title} placement="top" arrow enterDelay={400} disableInteractive>
      {isImport ? (
        <span className="pd-tree-node-action-tooltip-wrap">{button}</span>
      ) : (
        button
      )}
    </Tooltip>
  );
};

/**
 * The "Skills" branch of the composer sidebar: the root row, one row per skill
 * group, and the skills inside each expanded group.
 *
 * Extracted from `ContentEditorSidebar` because it is a self-contained feature -
 * it shares only the expand/collapse state and the course with the rest of the
 * tree, and it renders on the skills routes alone.
 */
const SkillsSidebarTree = ({
  course,
  courseSupportedLanguages,
  expandedNodes,
  toggle,
  navigate,
  isSkillsRoute,
  skillsSections,
  skillsActiveGroupId,
  skillsActiveSkillId,
  activeSkillsHashGroupId,
  skillsImporting,
  onImportAll,
  onImportAllFolder,
  onImportToGroup,
  onImportFolderToGroup,
}) => {
  if (!course?.id) return null;

  const skillsPath = buildCourseSkillsPath(course.id);
  const rootActive =
    !skillsActiveSkillId && !skillsActiveGroupId && !activeSkillsHashGroupId && isSkillsRoute;

  return (
    <div key={`skills-tree-${course.id}`}>
      <div className="pd-sidebar-tree-program-root">
        <TreeNode
          label="Skills"
          level={0}
          customPaddingLeft={PROGRAM_TREE_NODE_PAD}
          bold
          active={rootActive}
          onClick={() => navigate(skillsPath)}
          trailingActions={
            <>
              <TreeAction
                title="Import skills"
                isImport
                label="Import skills"
                icon={<ImportIcon />}
                disabled={skillsImporting}
                onClick={onImportAll}
              />
              <TreeAction
                title="Import skills from folder"
                isImport
                label="Import skills from folder"
                icon={<DriveFileMoveIcon />}
                disabled={skillsImporting}
                onClick={onImportAllFolder}
              />
              <TreeAction
                title="Add skill group"
                label="Add skill group"
                icon={<AddIcon />}
                onClick={() => navigate(buildCourseSkillGroupNewPath(course.id))}
              />
            </>
          }
        />
      </div>
      {skillsSections.length > 0 ? (
        <div className="pd-tree-children">
          {skillsSections.map((section) => {
            const groupKey = `skills-group-${section.id}`;
            const groupHasSkills = (section.skills || []).length > 0;
            const groupActive =
              !skillsActiveSkillId &&
              (String(skillsActiveGroupId || "") === String(section.id) ||
                String(activeSkillsHashGroupId || "") === String(section.id));
            // "Ungrouped" is a display bucket, not a real group - it has no edit page.
            const isUngrouped = String(section.id) === "ungrouped";

            return (
              <div key={groupKey}>
                <TreeNode
                  label={section.label}
                  customPaddingLeft={UNIT_TREE_NODE_PAD}
                  expanded={expandedNodes[groupKey]}
                  onToggle={() => toggle(groupKey)}
                  hasChildren={groupHasSkills}
                  active={groupActive}
                  onClick={
                    isUngrouped
                      ? () => navigate(skillsPath)
                      : () => navigate(buildCourseSkillGroupEditPath(course.id, section.id))
                  }
                  trailingActions={
                    <>
                      <TreeAction
                        title="Import skill"
                        isImport
                        label={`Import skill to ${section.label}`}
                        icon={<ImportIcon />}
                        disabled={skillsImporting}
                        onClick={() => onImportToGroup(section.id)}
                      />
                      <TreeAction
                        title="Import skill from folder"
                        isImport
                        label={`Import skill from folder to ${section.label}`}
                        icon={<DriveFileMoveIcon />}
                        disabled={skillsImporting}
                        onClick={() => onImportFolderToGroup(section.id)}
                      />
                      <TreeAction
                        title="Add skill"
                        label={`Add skill to ${section.label}`}
                        icon={<AddIcon />}
                        onClick={() =>
                          navigate(buildCourseSkillNewPath(course.id, isUngrouped ? null : section.id))
                        }
                      />
                    </>
                  }
                />
                {expandedNodes[groupKey] && groupHasSkills ? (
                  <div className="pd-tree-children">
                    {section.skills.map((skill) => (
                      <TreeNode
                        key={`skills-item-${skill.id}`}
                        label={skill.name || `Skill ${skill.id}`}
                        activationBadge={
                          <NodeActivationBadge
                            activeByLanguage={skill.active_by_language}
                            supportedLanguages={courseSupportedLanguages}
                          />
                        }
                        customPaddingLeft={CONTENT_ITEM_ROW_PAD}
                        expanded={false}
                        onToggle={undefined}
                        hasChildren={false}
                        active={String(skillsActiveSkillId || "") === String(skill.id)}
                        onClick={() => navigate(buildCourseSkillEditPath(course.id, skill.id))}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default SkillsSidebarTree;
